"use client";

/**
 * My Map pins - the full per-user saved-listing set (status + notes +
 * coordinates), one /api/watchlist fetch per session behind a
 * module-level store (same pattern as use-watchlist's id-set).
 *
 * Feeds three surfaces off one source: the My-Map map layer
 * (MyPinsMapView), the watchlist page, and the detail-panel editor.
 * Mutations (save / remove / patch) update the store optimistically-ish
 * (re-read the authoritative row from the server response) and keep the
 * heart id-set (use-watchlist) in sync so cards everywhere stay correct.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { captureEvent } from "@/lib/tracking";
import { API_BASE } from "@/lib/api";
import { openPinLimit } from "@/lib/use-pin-limit";
import { useAuth } from "@/lib/auth-context";
import { useUndo } from "@/lib/undo";
import { useT } from "@/lib/locale-context";
import { setWatchedLocal } from "@/lib/use-watchlist";
import type { PinStatus } from "@/lib/pin-status";

export interface MyPin {
  listing_id: string;
  created_at: string;
  price_at_add: number | null;
  url: string | null;
  thumbnail_url: string | null;
  location_name: string | null;
  complex_name: string | null;
  room_count: number | null;
  area_m2: number | null;
  leased: boolean;
  latitude: number | null;
  longitude: number | null;
  status: PinStatus | null;
  notes: string | null;
  pin_updated_at: string | null;
  active: boolean;
  current_price: number | null;
  price_delta_pct: number | null;
  ai_verdict_tier: string | null;
  ai_overpriced_pct: number | null;
}

/** Thrown by save() when the free pin cap is hit, so callers can show the
 *  Pro upsell instead of a generic error. */
export class PinLimitError extends Error {
  limit: number;
  constructor(limit: number) {
    super("pin_limit_reached");
    this.name = "PinLimitError";
    this.limit = limit;
  }
}

type Listener = () => void;

// Shared empty list while the store is unloaded: a fresh [] per render
// made every consumer that lists `pins` in deps re-run each render.
const NO_PINS: MyPin[] = [];

const store = {
  pins: null as MyPin[] | null,
  limit: 25,
  fetched: false,
  inflight: null as Promise<void> | null,
  listeners: new Set<Listener>(),
  notify() {
    this.listeners.forEach((l) => l());
  },
  set(pins: MyPin[], limit: number) {
    this.pins = pins;
    this.limit = limit;
    this.notify();
  },
  upsert(item: MyPin) {
    const next = (this.pins ?? []).filter((p) => p.listing_id !== item.listing_id);
    // Newest-first ordering matches the GET (created_at desc).
    this.pins = [item, ...next];
    this.notify();
  },
  drop(listingId: string) {
    this.pins = (this.pins ?? []).filter((p) => p.listing_id !== listingId);
    this.notify();
  },
};

/** Bridge FROM the heart toggle (use-watchlist): a heart tap creates or
 *  deletes the SAME watchlist row this store mirrors, so the drawer's
 *  My-Map editor must follow without a reload. Mirror of
 *  setWatchedLocal, which covers the opposite direction. Called via a
 *  dynamic import from use-watchlist (static one would be a cycle). */
export function syncPinsFromWatch(listingId: string, watched: boolean): void {
  if (store.pins === null) return; // not loaded yet - the first read is fresh anyway
  if (!watched) {
    store.drop(listingId);
  } else if (!store.pins.some((p) => p.listing_id === listingId)) {
    // The heart-created row's server snapshot fields (price, coords,
    // verdict) aren't known client-side - re-read the list.
    void fetchPins(true);
  }
}

async function fetchPins(force = false): Promise<void> {
  if (store.fetched && !force) return;
  if (store.inflight) return store.inflight;
  store.fetched = true;
  store.inflight = (async () => {
    try {
      const r = await fetch(`${API_BASE}/watchlist`, { credentials: "include" });
      if (!r.ok) throw new Error(String(r.status));
      const d = await r.json();
      store.set(d.items ?? [], typeof d.limit === "number" ? d.limit : 25);
    } catch {
      // Resolve to empty (so `ready` flips and surfaces show the empty
      // state instead of spinning forever) but allow a retry on the next
      // mount rather than caching the failure.
      store.fetched = false;
      if (store.pins === null) store.set([], store.limit);
    } finally {
      store.inflight = null;
    }
  })();
  return store.inflight;
}

/** The saved listings from the session store: the first caller fetches,
 *  everyone after reads the same list (the alerts hub included, which
 *  used to GET /watchlist a second time). `force` re-reads. Throws when
 *  the read failed. */
export async function loadPins(force = false): Promise<MyPin[]> {
  await fetchPins(force);
  if (!store.fetched) throw new Error("watchlist unavailable");
  return store.pins ?? [];
}

export function useMyPins() {
  const undo = useUndo();
  const t = useT();
  const { user } = useAuth();
  const [, bump] = useState(0);

  useEffect(() => {
    const l = () => bump((v) => v + 1);
    store.listeners.add(l);
    if (user) void fetchPins();
    return () => { store.listeners.delete(l); };
  }, [user]);

  const pins = store.pins;
  const ready = pins !== null;
  const count = pins?.length ?? 0;
  const atLimit = ready && count >= store.limit;

  // listing_ids the user marked "rejected" - the all-listings map layer
  // hides these so dismissed listings stop cluttering the browse.
  const rejectedIds = useMemo(
    () => new Set((pins ?? []).filter((p) => p.status === "rejected").map((p) => p.listing_id)),
    [pins],
  );

  const getPin = useCallback(
    (listingId: string | null | undefined): MyPin | null =>
      (listingId && store.pins?.find((p) => p.listing_id === listingId)) || null,
    [],
  );

  const save = useCallback(async (listingId: string): Promise<void> => {
    const r = await fetch(`${API_BASE}/watchlist`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ listing_id: listingId }),
    });
    if (r.status === 400) {
      const d = await r.json().catch(() => ({}));
      const detail = d?.detail;
      if (detail && typeof detail === "object" && detail.reason === "limit_reached") {
        const limit = typeof detail.limit === "number" ? detail.limit : store.limit;
        captureEvent("mymap_limit_reached", { limit });
        openPinLimit(limit); // global upsell sheet
        throw new PinLimitError(limit);
      }
    }
    if (!r.ok) throw new Error(String(r.status));
    setWatchedLocal(listingId, true);
    captureEvent("mymap_pin_saved", { listing_id: listingId });
    await fetchPins(true); // re-read so the new row carries server snapshot fields
  }, []);

  const remove = useCallback(async (listingId: string): Promise<void> => {
    store.drop(listingId); // optimistic
    setWatchedLocal(listingId, false);
    captureEvent("mymap_pin_removed", { listing_id: listingId });
    // The server hears about it when the undo toast runs out (owner,
    // 2026-10-05); undo re-reads the list, which still has the row.
    undo.schedule({
      message: t("undo.pin_removed"),
      commit: async () => {
        try {
          const r = await fetch(`${API_BASE}/watchlist/${encodeURIComponent(listingId)}`, { method: "DELETE", credentials: "include", keepalive: true });
          if (!r.ok) throw new Error(String(r.status));
        } catch {
          await fetchPins(true); // resync on failure
          setWatchedLocal(listingId, true);
        }
      },
      undo: async () => { setWatchedLocal(listingId, true); await fetchPins(true); },
    });
  }, [undo, t]);

  const patch = useCallback(
    async (listingId: string, body: { status?: PinStatus | null; notes?: string | null }): Promise<void> => {
      const r = await fetch(`${API_BASE}/watchlist/${encodeURIComponent(listingId)}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(String(r.status));
      const d = await r.json().catch(() => ({}));
      if (d?.item) store.upsert(d.item as MyPin);
      if (body.status !== undefined) {
        captureEvent("mymap_status_set", { status: body.status });
      }
    },
    [],
  );

  const reload = useCallback(() => fetchPins(true), []);

  return {
    signedIn: !!user,
    ready,
    pins: pins ?? NO_PINS,
    limit: store.limit,
    count,
    atLimit,
    rejectedIds,
    getPin,
    save,
    remove,
    patch,
    reload,
  };
}
